import { useQuery } from "@tanstack/react-query";
import {
  Loader2,
  Send,
  Check,
  Clock,
  XCircle,
  WifiOff,
  AlertTriangle,
  Radio,
} from "lucide-react";
import { useFilters } from "../../context/FilterContext";
import useApiHook from "../../api/Api";
import { formatNumber } from "../../../utils/interface";

interface MetricsData {
  invalid: number;
  total_absent_from_network: number;
  total_delivered: number;
  "total_delivered_to_network ": number;
  total_failed: number;
  total_pending: number;
  total_sent: number;
}

const ReportsMetrics = () => {
  const { startDate, endDate, year } = useFilters();

  const apiHook = useApiHook<MetricsData>(`/reports/metrics`, {
    method: "get",
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ["metrics", startDate, endDate, year],
    queryFn: () => {
      return apiHook
        .makeRequest({ start_date: startDate, end_date: endDate, year })
        .then((response) => {
          return response;
        });
    },
    enabled: true,
  });

  if (isLoading || apiHook.isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
        <span className="ml-2 text-lg font-medium">Loading metrics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        <p className="font-medium">Error loading metrics data</p>
        <p className="text-sm mt-1">
          Please try again or check your connection
        </p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-700">
        <p className="font-medium">No metrics data available</p>
        <p className="text-sm mt-1">Try adjusting your filter criteria</p>
      </div>
    );
  }

  // Calculate total for percentage calculations
  const total = data?.data?.data?.total_sent;

  // Define metrics cards with icons and enhanced styling
  const metricCards = [
    {
      title: "Total Sent",
      value: data?.data?.data?.total_sent,
      icon: <Send className="w-5 h-5 text-gray-500" />,
      key: "total_sent",
    },
    {
      title: "Total Delivered",
      value: data?.data?.data?.total_delivered,
      icon: <Check className="w-5 h-5 text-green-500" />,
      key: "total_delivered",
    },
    {
      title: "Total Pending",
      value: data?.data?.data?.total_pending,
      icon: <Clock className="w-5 h-5 text-amber-500" />,
      key: "total_pending",
    },
    {
      title: "Total Failed",
      value: data?.data?.data?.total_failed,
      icon: <XCircle className="w-5 h-5 text-red-500" />,
      key: "total_failed",
    },
    {
      title: "Absent From Network",
      value: data?.data?.data?.total_absent_from_network,
      icon: <WifiOff className="w-5 h-5 text-purple-500" />,
      key: "total_absent_from_network",
    },
    {
      title: "Invalid",
      value: data?.data?.data?.invalid,
      icon: <AlertTriangle className="w-5 h-5 text-orange-500" />,
      key: "invalid",
    },
    {
      title: "Delivered To Network",
      value: data?.data?.data?.total_delivered_to_network,
      icon: <Radio className="w-5 h-5 text-teal-500" />,
      key: "total_delivered_to_network",
    },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        {metricCards.map((card) => (
          <div
            key={card.key}
            className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm hover:shadow-md transition-all duration-200 flex flex-col"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-medium text-gray-700">
                {card.title}
              </h3>
              {card.icon}
            </div>
            <p className="text-3xl font-bold mt-auto">
              {formatNumber(card.value || 0)}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ReportsMetrics;
